import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, Save, Trophy, TrendingUp, Users, DollarSign, Filter, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';

const ADMIN_API = '/admin/bravx';

export default function AdminBravxPage() {
  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-8" data-testid="admin-bravx-page">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#D4AF37] mb-2 flex items-center gap-1.5" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            <Sparkles size={11} /> Sistema BRAVX
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter leading-none uppercase" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            Puntos y referidos
          </h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-2xl">
            Configurá la fórmula de puntos, monitoreá canjes y ajustá el comportamiento del sistema. Los cambios impactan compras futuras.
          </p>
        </div>
        <StatsRow />
        <SettingsCard />
        <ReferralsLog />
      </div>
    </AdminLayout>
  );
}

function StatsRow() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`${ADMIN_API}/stats`);
        setS(r.data);
      } catch (e) {
        toast.error('No se pudieron cargar las stats');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const items = [
    { label: 'Canjes totales', value: s?.total_redemptions, icon: <TrendingUp size={14} /> },
    { label: 'Usuarios con puntos', value: s?.active_bravx, icon: <Sparkles size={14} /> },
    { label: 'Usuarios con @', value: s?.users_with_username, icon: <Users size={14} /> },
    { label: 'Pts entregados (referrer)', value: s?.total_points_referrer, icon: <Trophy size={14} /> },
    { label: 'Pts entregados (buyer)', value: s?.total_points_buyer, icon: <Trophy size={14} /> },
    { label: 'Volumen total ARS', value: s?.total_amount, icon: <DollarSign size={14} />, prefix: '$' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="bravx-stats">
      {items.map((it, idx) => (
        <motion.div
          key={it.label}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: idx * 0.03 }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl p-4"
        >
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold mb-2">
            <span className="text-[#D4AF37]">{it.icon}</span>
            {it.label}
          </div>
          <p className="text-2xl font-black text-white" style={{ fontFamily: "'Unbounded', sans-serif" }}>
            {loading ? '—' : `${it.prefix || ''}${Number(it.value || 0).toLocaleString('es-AR')}`}
          </p>
        </motion.div>
      ))}
    </div>
  );
}

function SettingsCard() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`${ADMIN_API}/settings`);
      setS(r.data);
    } catch (e) {
      toast.error('No se pudieron cargar los settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`${ADMIN_API}/settings`, s);
      toast.success('Configuración guardada');
      load();
    } catch (e) {
      toast.error('No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !s) return null;

  const previewAmount = 2500;
  const base = Math.floor(previewAmount / Math.max(1, s.amount_threshold)) * Math.max(0, s.points_per_threshold);
  const previewReferrer = Math.round(base * (s.referrer_multiplier || 0));
  const previewBuyer = Math.round(base * (s.buyer_multiplier || 0)) + (s.buyer_bonus_points || 0);

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6" data-testid="bravx-settings-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Sparkles size={16} className="text-[#D4AF37]" /> Configuración del sistema
          </h3>
          <p className="text-xs text-zinc-400 mt-1">Cambios afectan TODAS las compras desde el guardado en adelante.</p>
        </div>
        <label className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wider font-bold text-zinc-300">
            {s.enabled ? 'ACTIVO' : 'PAUSADO'}
          </span>
          <Switch
            checked={!!s.enabled}
            onCheckedChange={(v) => setS({ ...s, enabled: v })}
            data-testid="bravx-enabled-switch"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <SettingNumber
          label="Puntos otorgados"
          hint={`cada $${s.amount_threshold || 100} ARS`}
          value={s.points_per_threshold}
          onChange={(v) => setS({ ...s, points_per_threshold: v })}
          testId="bravx-points-per"
          min={0}
        />
        <SettingNumber
          label="Umbral ARS"
          hint="threshold mínimo"
          value={s.amount_threshold}
          onChange={(v) => setS({ ...s, amount_threshold: v })}
          testId="bravx-amount-threshold"
          min={1}
        />
        <SettingNumber
          label="Bonus al comprador"
          hint="puntos extra (flat) por usar un código"
          value={s.buyer_bonus_points}
          onChange={(v) => setS({ ...s, buyer_bonus_points: v })}
          testId="bravx-buyer-bonus"
          min={0}
        />
        <SettingNumber
          label="Multiplicador referrer"
          hint="x al referido (1.0 = igual)"
          value={s.referrer_multiplier}
          onChange={(v) => setS({ ...s, referrer_multiplier: v })}
          testId="bravx-mult-ref"
          step={0.1}
          min={0}
        />
        <SettingNumber
          label="Multiplicador buyer"
          hint="x al comprador"
          value={s.buyer_multiplier}
          onChange={(v) => setS({ ...s, buyer_multiplier: v })}
          testId="bravx-mult-buy"
          step={0.1}
          min={0}
        />
        <div className="rounded-xl bg-black/30 border border-[#D4AF37]/30 p-4">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] font-bold mb-2">Preview</p>
          <p className="text-xs text-zinc-300 mb-1">Compra de ${previewAmount.toLocaleString('es-AR')}:</p>
          <p className="text-sm text-white">→ <span className="text-[#D4AF37] font-bold">+{previewReferrer}</span> al @referido</p>
          <p className="text-sm text-white">→ <span className="text-emerald-400 font-bold">+{previewBuyer}</span> al comprador</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        <SendMonthlyEmailsButton />
        <Button onClick={save} disabled={saving} className="bg-[#D4AF37] hover:bg-[#F5D25A] text-black font-bold" data-testid="bravx-save-settings">
          {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}
          Guardar configuración
        </Button>
      </div>
    </div>
  );
}

function SendMonthlyEmailsButton() {
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const trigger = async (force) => {
    setSending(true);
    try {
      const r = await api.post(`${ADMIN_API}/send-monthly-emails?force=${force ? 'true' : 'false'}`);
      const data = r.data || {};
      const sent = data.sent || 0;
      const skipped = data.skipped || 0;
      if (sent === 0 && (data.message || skipped === 0)) {
        toast.warning(data.message || 'Sin destinatarios elegibles.');
      } else {
        toast.success(`Top-3 ${data.label || ''}: ${sent} enviado(s), ${skipped} omitido(s)`);
      }
      setConfirming(false);
    } catch (e) {
      toast.error('No se pudo enviar el blast');
    } finally {
      setSending(false);
    }
  };

  if (!confirming) {
    return (
      <Button
        type="button"
        variant="outline"
        className="border-zinc-700 text-zinc-200 hover:text-white"
        onClick={() => setConfirming(true)}
        data-testid="bravx-send-monthly-btn"
      >
        Enviar emails Top-3
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-amber-300">¿Reenviar Top-3 del mes anterior?</span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="border-zinc-700 text-zinc-300"
        onClick={() => setConfirming(false)}
        disabled={sending}
        data-testid="bravx-send-monthly-cancel"
      >
        Cancelar
      </Button>
      <Button
        type="button"
        size="sm"
        className="bg-amber-500 hover:bg-amber-400 text-black font-bold"
        onClick={() => trigger(false)}
        disabled={sending}
        data-testid="bravx-send-monthly-confirm"
      >
        {sending ? <Loader2 size={13} className="animate-spin mr-1" /> : null}
        Enviar (idempotente)
      </Button>
      <Button
        type="button"
        size="sm"
        className="bg-red-500 hover:bg-red-400 text-white font-bold"
        onClick={() => trigger(true)}
        disabled={sending}
        data-testid="bravx-send-monthly-force"
      >
        Forzar reenvío
      </Button>
    </div>
  );
}

function SettingNumber({ label, hint, value, onChange, testId, step = 1, min = 0 }) {
  return (
    <div className="rounded-xl bg-black/30 border border-zinc-800 p-4">
      <label className="block text-[11px] uppercase tracking-wider font-bold text-zinc-300 mb-1">
        {label}
      </label>
      <Input
        type="number"
        step={step}
        min={min}
        value={value ?? 0}
        onChange={(e) => onChange(parseFloat(e.target.value || '0'))}
        className="bg-zinc-950 border-zinc-800"
        data-testid={testId}
      />
      {hint && <p className="text-[10px] text-zinc-500 mt-1.5">{hint}</p>}
    </div>
  );
}

function ReferralsLog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all'); // all | referral | self_purchase

  const load = async () => {
    try {
      const r = await api.get(`${ADMIN_API}/referrals?limit=300`);
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error('No se pudo cargar el log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) => {
    if (filter === 'referral' && it.type === 'self_purchase') return false;
    if (filter === 'self_purchase' && it.type !== 'self_purchase') return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const hay = [it.referrer_username, it.buyer_email, it.buyer_name, it.event_title].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  });

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6" data-testid="bravx-log-card">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Filter size={16} className="text-[#D4AF37]" /> Log de canjes
        </h3>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar @, email, evento..."
              className="pl-9 h-9 w-64 bg-zinc-950 border-zinc-800"
              data-testid="bravx-log-search"
            />
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-9 px-3 rounded-md bg-zinc-950 border border-zinc-800 text-xs"
            data-testid="bravx-log-filter"
          >
            <option value="all">Todos</option>
            <option value="referral">Solo referidos</option>
            <option value="self_purchase">Solo compras propias</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-10 text-zinc-500"><Loader2 className="mx-auto animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-zinc-500 text-sm">Sin canjes todavía.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 border-b border-zinc-800">
              <tr>
                <th className="text-left py-2 px-2">Fecha</th>
                <th className="text-left py-2 px-2">Tipo</th>
                <th className="text-left py-2 px-2">@Referido</th>
                <th className="text-left py-2 px-2">Comprador</th>
                <th className="text-left py-2 px-2">Evento</th>
                <th className="text-right py-2 px-2">Monto</th>
                <th className="text-right py-2 px-2">Pts ref.</th>
                <th className="text-right py-2 px-2">Pts buyer</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr key={it.id} className="border-b border-zinc-900 hover:bg-zinc-950/40">
                  <td className="py-2 px-2 text-zinc-400 text-xs whitespace-nowrap">
                    {it.created_at ? new Date(it.created_at).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td className="py-2 px-2">
                    <span
                      className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
                        it.type === 'self_purchase'
                          ? 'bg-blue-500/20 text-blue-300'
                          : 'bg-[#D4AF37]/20 text-[#D4AF37]'
                      }`}
                    >
                      {it.type === 'self_purchase' ? 'compra' : 'referido'}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-white">
                    {it.referrer_username ? `@${it.referrer_username}` : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="py-2 px-2 text-zinc-300 text-xs">
                    {it.buyer_name || it.buyer_email || '—'}
                  </td>
                  <td className="py-2 px-2 text-zinc-300 text-xs truncate max-w-[160px]">
                    {it.event_title || '—'}
                  </td>
                  <td className="py-2 px-2 text-right text-zinc-300 text-xs whitespace-nowrap">
                    ${Math.round(it.amount || 0).toLocaleString('es-AR')}
                  </td>
                  <td className="py-2 px-2 text-right text-[#D4AF37] font-bold">
                    +{it.points_awarded_to_referrer || 0}
                  </td>
                  <td className="py-2 px-2 text-right text-emerald-400 font-bold">
                    +{it.points_awarded_to_buyer || 0}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
